import { forwardRef, useEffect, useMemo, useState } from "react";
import useMessageDisplay from "../MessageDisplay";
import { isLazyValue } from "../../utils/lazyValue";
import { useCallback } from "react";
import { shallowEqual } from "react-redux";

const DEBOUNCE_INTERVAL = 500;
const LOADING_SENTINEL = {};

function hasLazyValue(lazyProps, props) {
    return lazyProps.some(prop => isLazyValue(props[prop]));
}

function buildResolve (lazyProps, props) {
    const init = {};
    for (const prop of lazyProps) {
        const v = props[prop];
        init[prop] = isLazyValue(v) ? LOADING_SENTINEL : v;
    }
    return init;
}

/**
 * Higher-order component that lets certain props be either plain values or "lazy" values
 * (made using lazyValue from the utils). Lazy props are awaited and their resolved values
 * are passed to the wrapped component under the same prop names. Optionally renders a
 * placeholder while loading, or keeps previous values until new ones arrive.
 *
 * Behavior:
 * - Props listed in `lazyProps` can be plain or lazy.
 * - If `keepPreviousWhileLoading` is false, a placeholder may render during loading.
 * - If true, the previous resolved values remain until new values resolve.
 *
 * @param {Function} Wrapped - React component to wrap (receives original props plus resolved values).
 * @param {Object} [opts] - the placeholder to render, the props to process and whether to display the placeholder on update
 * @returns {Function} A React component that resolves lazy props before rendering.
 *
 * @example
 * const UserName = ({ value }) => <span>{value}</span>;
 * const UserNameLazy = withLazyValueCapability(UserName, {
 *   lazyProps: ['value'],
 *   placeholder: <em>Loading…</em>,
 * });
 *
 * // You can pass `value` as a plain string...
 * <UserNameLazy value="Ada" />
 *
 * // ...or as a lazy value:
 * <UserNameLazy value={{ lazyValue(() => fetch('/api/name').then(r => r.text())) }} />
 */
export function withLazyValueCapability(Wrapped, opts = {}) {
    const {
        placeholder = <div>Loading…</div>, // ReactNode or (prevValue) => ReactNode,
        lazyProps: valueProps = ['value'],
        keepPreviousWhileLoading = false,
    } = opts;

    const WithLazyValue = forwardRef(function WithLazyValue(props, ref) {
        const { showError } = useMessageDisplay();
        const needsEval = useMemo(() => {
            return hasLazyValue(valueProps, props)
        }, [props]);

        const [resolved, setResolved] = useState(() => buildResolve(valueProps, props));
        const [loading, setLoading] = useState(needsEval);
        const safeSetResolved = useCallback(updater =>
            setResolved(prev => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                return shallowEqual(prev, next) ? prev : next;
            })
        , []);

        useEffect(() => {
            let cancelled = false;
            let loadingTimer;

            async function run() {
                if (needsEval) {
                    loadingTimer = setTimeout(() => {
                        if (cancelled) {
                            return;
                        }

                        setLoading(true);

                        if (!keepPreviousWhileLoading) {
                            safeSetResolved(buildResolve(valueProps, props));
                        }
                    }, DEBOUNCE_INTERVAL);

                    try {
                        const results = await Promise.all(valueProps.map(prop => isLazyValue(props[prop]) ? props[prop].evaluate() : Promise.resolve(props[prop])));
                        if (!cancelled) {
                            safeSetResolved(valueProps.reduce((obj, prop, idx) => { obj[prop] = results[idx]; return obj; }, {}));
                        }
                    } catch (e) {
                        showError(e);
                    } finally {
                        if (!cancelled) {
                            clearTimeout(loadingTimer);
                            setLoading(false);
                        }
                    }
                } else {
                    safeSetResolved(buildResolve(valueProps, props));
                    setLoading(false);
                }
            }

            run();

            return () => {
                cancelled = true; // ignore any late arrivals from this run()
                clearTimeout(loadingTimer);
            };
        }, [safeSetResolved, needsEval, props, showError, ...valueProps.map(n => props[n])]);

        if (loading && (!keepPreviousWhileLoading || Object.values(resolved).some(r => r === LOADING_SENTINEL))) {
            return typeof placeholder === "function" ? placeholder(resolved) : placeholder;
        }

        return <Wrapped {...props} {...resolved} ref={ref} />;
    });
    return WithLazyValue;
}